import { supabase } from '@/integrations/supabase/client';

export type TBRCategory = 'mood_read' | 'quick_read' | 'challenge_read' | 'palate_cleanser' | 'uncategorized';

interface Book {
  page_count?: number;
  categories?: string[];
  subjects?: string[];
}

interface UserContext {
  userId: string;
  recentReads?: Array<{
    categories?: string[];
    page_count?: number;
  }>;
  averageRating?: number;
}

const MOOD_READ_GENRES = [
  'romance', 'cozy mystery', 'light fantasy', 'contemporary', 
  'feel-good', 'comfort read', 'beach read', 'chicklit'
];

const CHALLENGE_READ_GENRES = [
  'literary fiction', 'philosophy', 'theory', 'academic',
  'non-fiction', 'history', 'biography', 'science', 'classics'
];

export async function categorizeBook(
  book: Book,
  userContext: UserContext
): Promise<TBRCategory> {
  const { page_count, categories = [], subjects = [] } = book;
  
  // Combine categories and subjects for genre analysis
  const allGenres = [...categories, ...subjects].map(g => g.toLowerCase());
  
  // Quick Read: Less than 250 pages
  if (page_count && page_count < 250) {
    return 'quick_read';
  }
  
  // Mood Read: Light, comforting genres
  const isMoodRead = allGenres.some(genre => 
    MOOD_READ_GENRES.some(moodGenre => genre.includes(moodGenre))
  );
  if (isMoodRead) {
    return 'mood_read';
  }
  
  // Challenge Read: Dense genres OR very long books
  const isChallengeGenre = allGenres.some(genre =>
    CHALLENGE_READ_GENRES.some(challengeGenre => genre.includes(challengeGenre))
  );
  if (isChallengeGenre || (page_count && page_count > 500)) {
    return 'challenge_read';
  }
  
  // Palate Cleanser: Different from recent reads and moderate length
  if (userContext.recentReads && userContext.recentReads.length > 0) {
    const recentGenres = userContext.recentReads
      .flatMap(read => read.categories || [])
      .map(g => g.toLowerCase());
    
    const isDifferentGenre = allGenres.length > 0 && 
      !allGenres.some(genre => recentGenres.some(recent => recent.includes(genre)));
    
    const isModerateLength = page_count && page_count >= 200 && page_count <= 350;
    
    if (isDifferentGenre && isModerateLength) {
      return 'palate_cleanser';
    }
  }
  
  return 'uncategorized';
}

export async function getCategoryLabel(category: TBRCategory): Promise<string> {
  const labels: Record<TBRCategory, string> = {
    mood_read: 'Mood Read',
    quick_read: 'Quick Read',
    challenge_read: 'Challenge Read',
    palate_cleanser: 'Palate Cleanser',
    uncategorized: 'Uncategorized'
  };
  return labels[category];
}

export async function getCategoryEmoji(category: TBRCategory): Promise<string> {
  const emojis: Record<TBRCategory, string> = {
    mood_read: '💖',
    quick_read: '📖',
    challenge_read: '🏋️',
    palate_cleanser: '🍃',
    uncategorized: '📚'
  };
  return emojis[category];
}

export async function fetchUserContext(userId: string): Promise<UserContext> {
  // Fetch recent reads (last 5)
  const { data: recentReads } = await supabase
    .from('user_books')
    .select('books(categories, page_count)')
    .eq('user_id', userId)
    .eq('shelf', 'read')
    .order('finished_at', { ascending: false })
    .limit(5);
  
  const recentReadsData = recentReads?.map(ub => ({
    categories: (ub.books as any)?.categories || [],
    page_count: (ub.books as any)?.page_count
  })) || [];
  
  return {
    userId,
    recentReads: recentReadsData
  };
}
